import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    EmployeesClient,
    NestleDetailsDto,
    NestleStatus,
    PermissionType,
    UpdateNestleDetailsCommand
} from '@services';
import { DxDateBoxModule, DxSelectBoxModule, DxTextBoxModule, DxValidatorModule } from 'devextreme-angular';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import notify from 'devextreme/ui/notify';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DATE_FORMAT, YES_NO_OPTIONS } from 'src/app/shared/constants';
import EnumUtils from 'src/app/shared/enum-utils';
import { NestleStatusPipe } from 'src/app/pipes/nestle-status.pipe';
import { NestleDeactivationHistoryComponent } from '../nestle-deactivation-history/nestle-deactivation-history.component';

@Component({
    selector: 'app-nestle-details',
    templateUrl: './nestle-details.component.html',
    standalone: true,
    imports: [CommonModule, DxDateBoxModule, DxSelectBoxModule, DxTextBoxModule, DxValidatorModule, DxiValidationRuleComponent, NestleStatusPipe, NestleDeactivationHistoryComponent]
})
export class NestleDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    protected authService = inject(AuthService);

    @ViewChild(NestleDeactivationHistoryComponent) private historyComponent?: NestleDeactivationHistoryComponent;

    @Input() userId: string;
    @Input() forceReadOnly = false;
    // Unsaved SAP/DWA/NextView edits from Employment Details; undefined falls back to the saved values.
    @Input() systemAccess?: boolean;
    @Input() status?: NestleStatus;
    @Output() isDataChanged = new EventEmitter<boolean>();

    details: NestleDetailsDto;
    // Save keeps every field optional; only a Nestle confirmation turns this on to require them all.
    requireAllFields = false;

    protected readonly dateFormat = DATE_FORMAT;
    protected readonly yesNoOptions = YES_NO_OPTIONS;
    protected readonly requiredOnConfirm = (e: { value: unknown }): boolean =>
        !this.requireAllFields || (e.value !== null && e.value !== undefined && String(e.value).trim() !== '');

    ngOnInit() {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.employeesClient.getNestleDetails(this.userId).subscribe({
            next: (data) => this.details = data,
            error: () => notify('Failed to load Nestle details', 'error', 3000)
        });
        this.historyComponent?.loadData();
    }

    public save(): Observable<void> {
        if (this.isReadOnly || !this.details) return of(void 0);

        const command = new UpdateNestleDetailsCommand({
            userId: this.userId,
            adpHireDate: this.details.adpHireDate,
            newHire: this.details.newHire,
            agency: this.details.agency,
            positionId: this.details.positionId,
            sapSuccessFactorId: this.details.sapSuccessFactorId,
            employeeId: this.details.employeeId,
            nestleUserId: this.details.nestleUserId,
            email: this.details.email,
            nextPasswordExpiryDate: this.details.nextPasswordExpiryDate
        });

        return this.employeesClient.updateNestleDetails(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update Nestle details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange(e: { event?: Event }) {
        // A reload after a profile action changes the values programmatically; that is not an edit.
        if (!e.event) return;

        this.isDataChanged.emit(true);
    }

    private get isDeactivationStatus(): boolean {
        return this.isLocked || this.status === NestleStatus.TerminationPending || this.status === NestleStatus.DeactivationPending;
    }

    private get isLocked(): boolean {
        return this.status === NestleStatus.Terminated || this.status === NestleStatus.Inactive;
    }

    protected get isPasswordResetRequired(): boolean {
        return this.status === NestleStatus.PasswordResetRequired;
    }

    protected getStatusBadgeClass(status: NestleStatus): string {
        return EnumUtils.getNestleStatusBadgeClass(status);
    }

    // Delimiting the position clears every access flag, so a termination or deactivation keeps the section visible on its own.
    get isVisible(): boolean {
        return !!this.details && (this.isDeactivationStatus || (this.systemAccess ?? this.details.hasSystemAccess));
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || this.isLocked || !this.authService.canEditProfileSection(this.userId, PermissionType.EmployeeNestleDetailsOwn, PermissionType.EmployeeNestleDetailsOthers);
    }
}
